import type { ReactNode } from 'react';
import type { Permission } from '@/schemas/auth/auth.schemas';
import { useAuth } from '@/contexts/AuthProvider';
import { hasEffectivePermission } from '@/lib/permissions';

type PermissionGateProps =
  | {
      /** Single permission required to render children */
      permission: Permission;
      permissions?: never;
      requireAll?: never;
      /** Rendered when the user lacks the required permission(s) */
      fallback?: ReactNode;
      children: ReactNode;
    }
  | {
      permission?: never;
      /** Multiple permissions to check */
      permissions: Permission[];
      /** When true, ALL permissions must be present (default: false — any match) */
      requireAll?: boolean;
      /** Rendered when the user lacks the required permission(s) */
      fallback?: ReactNode;
      children: ReactNode;
    };

/**
 * Conditionally renders children based on the current user's permissions.
 *
 * Usage:
 *   <PermissionGate permission="users:write">
 *     <CreateUserButton />
 *   </PermissionGate>
 *
 *   <PermissionGate permissions={['assets:read', 'assets:write']} requireAll>
 *     <AssetEditor />
 *   </PermissionGate>
 */
export function PermissionGate({
  permission,
  permissions,
  requireAll = false,
  fallback = null,
  children,
}: PermissionGateProps): React.JSX.Element {
  const { currentTenantAccess } = useAuth();

  let permitted = false;

  if (currentTenantAccess) {
    if (permission) {
      permitted = hasEffectivePermission(currentTenantAccess.permissions, permission);
    } else {
      const permsList: Permission[] = permissions ?? [];
      if (permsList.length > 0) {
        permitted = requireAll
          ? permsList.every(p => hasEffectivePermission(currentTenantAccess.permissions, p))
          : permsList.some(p => hasEffectivePermission(currentTenantAccess.permissions, p));
      }
    }
  }

  return <>{permitted ? children : fallback}</>;
}
